import Loader from "@shared/components/Loader";
import React, { useState, useRef, useEffect } from "react";

import { useUser } from "@services/UserContext";
import { apiBaseUrl, googleApiKey, googleAppId } from "@constants";
import { useParams } from "react-router-dom";
import { OneDrivePicker } from "./OneDrivePicker";
import { useOneDriveUpload } from "./useOneDrivePicker";
import { OneDriveSelectedItem } from "@services/OneDriveService";
import { useOneDrivePicker } from "./oneDriveAuth";

interface CloudSyncDropdownProps {
  triggerRefreshGDriveFiles: () => void;
  triggerRefreshOneDriveFiles: () => void;
}

const CloudSyncDropdown: React.FC<CloudSyncDropdownProps> = ({
  triggerRefreshGDriveFiles,
  triggerRefreshOneDriveFiles,
}) => {
  const { projectId } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [oneDriveLoading, setOneDriveLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const { googleDriveToken, setGoogleDriveToken } = useUser();

  const [openOneDriveModal, setOpenOneDriveModal] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleGoogleDrive = async () => {
    if (!googleDriveToken) {
      const res = await fetch(
        `${apiBaseUrl}/gdrive/auth-url/?project_id=${projectId}`,
        {
          credentials: "include",
        }
      );
      const data = await res.json();
      window.location.href = data.auth_url;
      return;
    }

    const view = new window.google.picker.DocsView()
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true)
      .setMimeTypes(
        [
          "application/vnd.google-apps.folder",
          "application/pdf",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.google-apps.document",
          "application/vnd.google-apps.spreadsheet",
        ].join(",")
      );

    const picker = new window.google.picker.PickerBuilder()
      .addView(view)
      .setDeveloperKey(googleApiKey)
      .setAppId(googleAppId)
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(googleDriveToken)
      .setCallback(async (data: any) => {
        if (data.action === window.google.picker.Action.PICKED) {
          const selectedItems = data.docs;

          const folders = selectedItems.filter(
            (item: any) =>
              item.mimeType === "application/vnd.google-apps.folder"
          );
          const files = selectedItems.filter(
            (item: any) =>
              item.mimeType !== "application/vnd.google-apps.folder"
          );

          // ✅ Validate selection: Only one folder OR multiple files
          if (folders.length > 0 && files.length > 0) {
            alert("You cannot select both folders and files at the same time.");
            return;
          }
          if (folders.length > 1) {
            alert("You can only select one folder at a time.");
            return;
          }

          const payload =
            folders.length === 1
              ? [
                  {
                    id: folders[0].id,
                    name: folders[0].name,
                    mimeType: folders[0].mimeType,
                  },
                ]
              : files.map((f: any) => ({
                  id: f.id,
                  name: f.name,
                  mimeType: f.mimeType,
                }));

          try {
            setIsLoading(true);

            const res = await fetch(
              `${apiBaseUrl}/gdrive/upload-from-drive/?project_id=${projectId}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify(payload),
              }
            );

            const result = await res.json();

            if (!res.ok) {
              throw new Error(result.detail || "Upload failed");
            }

            triggerRefreshGDriveFiles();

            setOpen(false);
            // optionally call `onFilesUploaded()` if needed
          } catch (err: any) {
            alert("Upload error: " + err.message);
          } finally {
            setIsLoading(false);
          }
        }
      })
      .build();

    picker.setVisible(true);
  };

  const {
    data,
    loading,
    error,
    fetchData: uploadedItems,
  } = useOneDriveUpload<any>();

  const handleUpload = async (items: OneDriveSelectedItem[]) => {
    if (!projectId) return;

    setIsLoading(true);
    await uploadedItems(projectId, items);
    triggerRefreshOneDriveFiles();
    setOpenOneDriveModal(false);
    setIsLoading(false);
  };

  const openDrivePicker = () => {
    setOpenOneDriveModal(true);
  };

  const { validateAndOpenOneDrivePicker } = useOneDrivePicker({
    openDrivePicker,
  });

  const handleOneDrivePicker = async () => {
    setOneDriveLoading(true);
    await validateAndOpenOneDrivePicker();
    setOneDriveLoading(false);
  };

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/gdrive/picker-token/`, {
          credentials: "include",
        });
        const data = await res.json();
        if (data.access_token) {
          setGoogleDriveToken(data.access_token);
        }
      } catch (err) {
        // user not connected yet, ignore
      }
    };
    fetchToken();
  }, [setGoogleDriveToken]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          disabled={isLoading}
          className="border text-md w-[12rem] border-[#CFD4DC] py-2 rounded-md flex cursor-pointer hover:bg-gray-50 transition px-4 items-center space-x-2 justify-between disabled:cursor-not-allowed"
        >
          <img src="/cloud.svg" className="w-4" alt="" />
          <span className="font-semibold text-sm mt-0 whitespace-nowrap inline-block">
            Sync with Cloud
          </span>
          {isLoading ? (
            <Loader color="#2162EB" style="" />
          ) : (
            <img
              src="/arrow_dropdown_black.svg"
              className={`w-3 flex-shrink-0 transition-transform duration-200 ${
                open ? "rotate-180" : ""
              }`}
              alt=""
            />
          )}
        </button>

        {open && !isLoading && (
          <div className="absolute left-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-50">
            <button
              type="button"
              onClick={handleGoogleDrive}
              className="flex items-center w-full px-4 py-2 hover:bg-gray-100 transition space-x-2"
            >
              <img src="/icon_google_cloud.svg" className="w-5 h-5" alt="" />
              <span className="text-gray-700 text-sm 2xl:text-base">
                Google Drive
              </span>
            </button>
            <button
              type="button"
              onClick={handleOneDrivePicker}
              className="flex items-center w-full px-4 py-2 hover:bg-gray-100 border-t transition space-x-2"
            >
              <img src="/icon_ms_onedrive.svg" className="w-5 h-5" alt="" />
              <span className="text-gray-700 text-sm 2xl:text-base flex items-center space-x-2">
                <span>OneDrive</span>
                {oneDriveLoading && <Loader color="#2162EB" />}
              </span>
            </button>
          </div>
        )}
      </div>
      <OneDrivePicker
        isOpen={openOneDriveModal}
        onClose={() => setOpenOneDriveModal(false)}
        onConfirm={(items) => {
          // console.log("Selected OneDrive items:", items);
          handleUpload(items);
        }}
      />
    </>
  );
};

export default CloudSyncDropdown;
